import { Link } from '@tanstack/react-router';
import { Construction } from 'lucide-react';
import type { ReactNode } from 'react';
import { Button } from '../../components/Button';
import { SystemState } from '../../components/system-states/SystemState';
import { NAV_ITEMS, type ModuleId } from '../../shell/nav-config';

// One placeholder per module, so the permission-driven module bar has somewhere
// to navigate to before the feature slices arrive. Each slice replaces its own.

/** Which slice builds each module, taken from the master plan's build order. */
const BUILT_BY: Readonly<Record<ModuleId, string>> = {
  // S5 replaced this one with real screens; the entry stays because the map is
  // keyed by every module id, and a missing key is a compile error rather than a
  // blank screen somebody finds later.
  organisation: 'The org tree, position ledger and headcount plan.',
  dashboard: 'S27 and S28',
  requisitions: 'S9 and S10',
  jobs: 'S11 and S12',
  candidates: 'S14 and S25',
  // S17 builds it; never a placeholder, keyed only because the map is total.
  assessments: 'S17 and S18',
  interviews: 'S18 to S21',
  tasks: 'S26',
  reports: 'S29',
  admin: 'S4 and S31',
  // S10 builds all three; never a placeholder, keyed only because the map is total.
  my_actions: 'S10',
  my_openings: 'S10',
  request_hire: 'S10',
};

export function PlaceholderPage({ moduleId }: { moduleId: ModuleId }): ReactNode {
  const item = NAV_ITEMS.find((navItem) => navItem.id === moduleId);
  const label = item?.label ?? 'This module';

  return (
    <SystemState
      icon={Construction}
      title={`${label} arrives in ${BUILT_BY[moduleId]}`}
      body={
        'S2 builds the shell, the design system and the shared components that every screen ' +
        'sits inside. The component demo page shows all of them working.'
      }
      actions={
        <Link to="/demo">
          <Button variant="solid">Open the component demo</Button>
        </Link>
      }
    />
  );
}
